import { SecondaryTabs, TableV2 as Table } from "@asayinc/component-library";
import { Stack } from "@mui/material";
import {
  FilterList,
  Downloading,
  DownloadingOutlined,
  History,
  HistoryOutlined,
  Verified,
  VerifiedOutlined,
  WatchLater,
  WatchLaterOutlined,
} from "@mui/icons-material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { LOCATION_ANALYSIS_EVENT_TYPE_OPTIONS } from "src/constants/options/locationAnalysisEventOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useLocationAnalysisEventsQuery } from "src/store/locationAnalysisEvent";
import { getColumnData, getColumns, getRowData } from "./utils";
import { ILocationAnalysisEvent } from "src/store/locationAnalysisEvent/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";

export type LocationAnalysisEventsView =
  | "pending_approval"
  | "pending_delivery"
  | "recently_delivered"
  | "historical";

export const LOCATION_ANALYSIS_EVENTS_VIEW: {
  [key: string]: LocationAnalysisEventsView;
} = {
  PENDING_APPROVAL: "pending_approval",
  PENDING_DELIVERY: "pending_delivery",
  RECENTLY_DELIVERED: "recently_delivered",
  HISTORICAL: "historical",
};

export function LocationAnalysisEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm();
  const view = (searchParams.get(TABLE_SEARCH_PARAMS.VIEW) ||
    LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL) as LocationAnalysisEventsView;
  const { type } = useTableSearchParams();

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useLocationAnalysisEventsQuery({
    offset,
    limit,
    ordering,
    type,
    tab: view || LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: ILocationAnalysisEvent) => {
    const { id } = event;
    navigate(`/location-analysis-events/${id}/details`);
  };

  const handleSecondaryTabChange = (tab: string) => {
    const newParams = new URLSearchParams({
      view: tab,
    });
    setSearchParams(newParams);
  };

  const columns = useMemo(() => getColumns(view), [view]);
  const columnData = useMemo(() => {
    return getColumnData({
      getSort,
      onOrderingChange,
    });
  }, [getSort, onOrderingChange]);
  const rowData = getRowData(data?.results || [], handleRowClick);

  const filterList = (
    <FormProvider {...methods}>
      <TableMultiSelectFilter
        fieldName={TABLE_SEARCH_PARAMS.TYPE}
        label="Types"
        options={LOCATION_ANALYSIS_EVENT_TYPE_OPTIONS}
        queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        view={view}
      />
    </FormProvider>
  );

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Stack>
        <SecondaryTabs
          sx={{ mb: 8, mr: "auto" }}
          tabs={[
            {
              label: "Pending approval",
              value: LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL,
              icon:
                view === LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL ? (
                  <WatchLater style={{ color: "#1968FF" }} />
                ) : (
                  <WatchLaterOutlined />
                ),
            },
            {
              label: "Pending delivery",
              value: LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY,
              icon:
                view === LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY ? (
                  <Downloading style={{ color: "#1968FF" }} />
                ) : (
                  <DownloadingOutlined />
                ),
            },
            {
              label: "Recently delivered",
              value: LOCATION_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED,
              icon:
                view === LOCATION_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED ? (
                  <Verified style={{ color: "#1968FF" }} />
                ) : (
                  <VerifiedOutlined />
                ),
            },
            {
              label: "Historical",
              value: LOCATION_ANALYSIS_EVENTS_VIEW.HISTORICAL,
              icon:
                view === LOCATION_ANALYSIS_EVENTS_VIEW.HISTORICAL ? (
                  <History style={{ color: "#1968FF" }} />
                ) : (
                  <HistoryOutlined />
                ),
            },
          ]}
          activeTab={view}
          clickHandler={handleSecondaryTabChange}
        />
      </Stack>
      <Table
        count={data?.count || 0}
        columnData={columnData}
        columns={columns}
        isFetching={isFetching}
        isLoading={isLoading}
        noResultsData={{
          title: "No Location Analysis Events found",
          subtitle:
            "Sorry, there are no Location Analysis Events that match your applied filters.",
        }}
        paginateProps={paginateProps}
        rows={rowData}
        tableMinWidth={1100}
        title={
          view === LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL
            ? "Pending Approval Location Analysis Events"
            : view === LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY
            ? "Pending Delivery Location Analysis Events"
            : view === LOCATION_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED
            ? "Recently Delivered Location Analysis Events"
            : "Historical Location Analysis Events"
        }
        titleVariant="subtitle2"
        toolbarInside
        toolbarCollapse={{
          Icon: FilterList,
          collapseContent: filterList,
          isFlat: true,
        }}
      />
    </ScrollContainer>
  );
}
